import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileSpreadsheet, Loader2, SlidersHorizontal, Hash, CalendarRange, Calendar } from 'lucide-react';
import api, { apiLongOperation, exportStreamBaseURL } from '../utils/api';
import { brandOptionsPath } from '../utils/brandOptionsSource';
import { useClient } from '../contexts/ClientContext';
import { buildExportColumns, injectDataExportCalculadas, ExportColumnDescriptor } from '../utils/exportColumns';
import { resolveTemplateOrder } from '../services/columnTemplates';
import { resolveExportAttrs, toLogicalExportId, DATA_EXPORT_BASE_ORDER } from '../services/columnExportAttrs';
import ProgressToast from './ProgressToast';
import FilterBox, { FilterSlot } from './filters/FilterBox';
import FieldLabel from './filters/FieldLabel';
import { EXPORTAR_CONFIG, DimensionKey, FilterControlKey } from '../config/filterBoxConfigs';
import { useFilterBoxState, InlineSourceData } from '../hooks/useFilterBoxState';
import { useFacetedFilters } from '../hooks/useFacetedFilters';
import { registerExportBridge, ExportAxesSnapshot } from './export/exportFilterBridge';
import ExportJobsLog from './export/ExportJobsLog';

/**
 * ExportPanel — sección EXPORTAR (PT-#61) v1.
 *
 * Exporta a un único XLSX las filas de Publicaciones de VARIAS tomas (resueltas por
 * cantidad o por período, misma semántica que Tendencias). El acotado se hace con LA MISMA
 * CAJA DE FILTROS QUE TENDENCIAS (no la de Publicaciones): FilterBox con las 5 dimensiones
 * combinables (marca/sitio/categoría/subcategoría/segmento) + cascade treeMode + el alcance
 * temporal como slot `period`. El usuario ve el total de filas en vivo ("N filas"); un único
 * botón primario dispara el export.
 *
 * Por qué Trends y no Pubs: EXPORTAR es inherentemente multi-toma; la caja de Pubs es
 * single-toma y no tiene selector de alcance temporal. Ver corrección de Gabriel y blueprint
 * en `docs/pendientes/PT61-exportar-caja-filtros-trends.md` (reusa CategoryPriceTrendCard).
 *
 * Backend: /api/export/multi-toma(-count) — reusa ListingService.getListing + enrichment
 * compartido (mismo hot path que Publicaciones), aceptando `filters` en la shape de Pubs.
 * El armado del XLSX reusa exportToExcel.ts (numérico es-AR, HYPERLINK, headers estilados).
 *
 * v1 conocido (ajustes futuros): exporta TODAS las columnas habilitadas del cliente (sin
 * selector de columnas). El alcance temporal ya soporta presets Y rango Personalizado
 * (Desde/Hasta), idéntico a Tendencias.
 */

interface ExportPanelProps {
  /** Toma actual (para leer el schema de columnas + facets de las dimensiones). El export
   *  resuelve sus propias tomas server-side. */
  tableName: string;
  /** Columna de marca resuelta por el cliente (misma que arma Publicaciones en Dashboard). */
  brandFilterColumn: string;
  /** Columna/clave de categoría resuelta por el cliente (`categoriaColumnName || 'Cat'`). */
  categoryFilterColumn: string;
  /** PT-#61 — Abre el modal combinado de filtros guardados (guardar+cargar+borrar). Se
   *  renderiza DENTRO del header del FilterBox, mismo lugar que Publicaciones/Estadísticas/
   *  Tendencias. Sin este prop el botón no aparece (mismo gate que las otras secciones). */
  onOpenSavedFilters?: () => void;
  /** Deshabilita el botón mientras cargan los filtros guardados. */
  loadingSavedFilters?: boolean;
}

type TomaMode = 'count' | 'period';

// PT-#72: límites del input numérico de "Tomas" (editable, antes lista fija).
const TOMAS_LIMIT_MIN = 1;
const TOMAS_LIMIT_MAX = 90;
const TOMAS_LIMIT_DEFAULT = 5;

// PT-#72: límite único del feature (espeja MAX_EXPORT_ROWS del server). La caja de aviso SOLO
// se muestra cuando el estimado lo supera (el export se trunca a este tope). Sin leyendas de
// "puede tardar / archivo grande" por debajo del límite.
const MAX_EXPORT_ROWS = 700000; // PT-#64.a: 500k→700k (capacidad medida ~460s, bajo el techo de 600s).
// PT-#72: presets de período en días (15/30/60/90) sin el prefijo "Últimos", + "Intervalo"
// (rango explícito Desde/Hasta, antes "Personalizado"). El server resuelve `Nd` genéricamente.
const PERIOD_PRESETS: { value: string; labelKey: string; fallback: string }[] = [
  { value: 'custom', labelKey: 'export.period_interval', fallback: 'Intervalo' },
  { value: 'current_month', labelKey: 'export.period_current_month', fallback: 'Mes actual' },
  { value: '15d', labelKey: 'export.period_15d', fallback: '15 días' },
  { value: '30d', labelKey: 'export.period_30d', fallback: '30 días' },
  { value: '60d', labelKey: 'export.period_60d', fallback: '60 días' },
  { value: '90d', labelKey: 'export.period_90d', fallback: '90 días' },
];

/** YYYY-MM-DD en hora local (los inputs date usan fecha local, no UTC). */
function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Primer día del mes actual (YYYY-MM-DD, hora local). */
function firstDayOfCurrentMonthYmd(): string {
  const d = new Date();
  return localYmd(new Date(d.getFullYear(), d.getMonth(), 1));
}
/** Último día del mes actual (YYYY-MM-DD, hora local). El día 0 del mes siguiente = último del actual. */
function lastDayOfCurrentMonthYmd(): string {
  const d = new Date();
  return localYmd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

/** Parsea la respuesta de los endpoints distinct-* a options planas (multi-toma, sin counts). */
function parseDistinctOptions(data: any): string[] {
  const raw: any[] =
    (Array.isArray(data?.subcategories) && data.subcategories) ||
    (Array.isArray(data?.segmentos) && data.segmentos) ||
    (Array.isArray(data?.values) && data.values) ||
    [];
  const options: string[] = [];
  for (const v of raw) {
    if (typeof v === 'string') options.push(v);
    else if (v && typeof v.value === 'string') options.push(v.value);
  }
  return options.sort((a, b) => {
    if (a === '(vacío)') return 1;
    if (b === '(vacío)') return -1;
    return a.localeCompare(b);
  });
}

const ExportPanel: React.FC<ExportPanelProps> = ({ tableName, brandFilterColumn, categoryFilterColumn, onOpenSavedFilters, loadingSavedFilters = false }) => {
  const { selectedClient } = useClient(); // PT-#87 PR-d: enable_tags → fuente de Marca
  const { t } = useTranslation();

  // ── Alcance temporal (Acotar por: Tomas / Período) ──────────────────────────
  // PT-#72: default = Período + Intervalo, con Desde=hace 1 semana y Hasta=hoy.
  const [mode, setMode] = useState<TomaMode>('period');
  const [tomasLimit, setTomasLimit] = useState<number>(TOMAS_LIMIT_DEFAULT);
  const [periodPreset, setPeriodPreset] = useState<string>('custom');
  const [dateFrom, setDateFrom] = useState<string>(() => localYmd(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))); // hace 1 semana
  const [dateTo, setDateTo] = useState<string>(() => localYmd(new Date()));     // hoy (inclusive)

  // ── Columnas a exportar: derivadas del columnConfig del cliente (MISMO criterio
  //    que PUBS via buildExportColumns). v1 exporta TODAS las habilitadas del cliente. ──
  const [exportSchema, setExportSchema] = useState<any[]>([]);
  const [exportColumnConfig, setExportColumnConfig] = useState<any>(null);
  const exportColumns = useMemo<ExportColumnDescriptor[]>(() => {
    // PT-#67: las calculadas (toma/id_pulpou/url) se modelan como columnas del template
    // — se inyectan acá como descriptores para que participen del orden/on-off del template
    // y del generador (antes se pusheaban hardcodeadas al armar el stream). `semana` ya la
    // trae buildExportColumns pegada a `fecha`.
    const base = injectDataExportCalculadas(buildExportColumns(exportSchema, exportColumnConfig));
    // PT-#67: EXPORTAR usa el template `data_export` para el orden + on/off de
    // columnas. Sin template → base tal cual (v1: todas las habilitadas). El
    // template descarta las columnas enabled=false y ordena; las columnas nuevas
    // (no presentes en el template) se appendean al final vía resolveTemplateOrder.
    const tpl = exportColumnConfig?.templates?.data_export;
    const byId = new Map(base.map((c) => [c.id, c]));
    // PT-#70: sin template guardado → orden BASE del reporte Samsung (DATA_EXPORT_BASE_ORDER):
    // las 23 columnas conocidas primero en ese orden, luego cualquier columna extra del cliente
    // al final (en el orden de buildExportColumns). Nunca se pierde una columna disponible.
    if (!tpl || !Array.isArray(tpl.items) || tpl.items.length === 0) {
      // Matchear por id LÓGICO: los ids de los descriptores son las claves core/native del
      // cliente (ej. `discount`, `segment`, `udm_source`), mientras que DATA_EXPORT_BASE_ORDER
      // usa ids lógicos (`descuento`, `segmento`, `udm`). Sin normalizar, esas columnas no
      // matchean y caen al final fuera del orden del reporte de referencia (PT-#70.a).
      const byLogical = new Map<string, ExportColumnDescriptor>();
      for (const c of base) {
        const lid = toLogicalExportId(c.id);
        if (!byLogical.has(lid)) byLogical.set(lid, c);
      }
      const inBase = DATA_EXPORT_BASE_ORDER
        .map((id) => byLogical.get(id))
        .filter((c): c is ExportColumnDescriptor => !!c);
      const usedLogical = new Set(inBase.map((c) => toLogicalExportId(c.id)));
      const rest = base.filter((c) => !usedLogical.has(toLogicalExportId(c.id)));
      const seeded = [...inBase, ...rest];
      return seeded.length > 0 ? seeded : base;
    }
    const orderedIds = resolveTemplateOrder(base.map((c) => c.id), tpl);
    const ordered = orderedIds
      .map((id) => byId.get(id))
      .filter((c): c is ExportColumnDescriptor => !!c);
    return ordered.length > 0 ? ordered : base;
  }, [exportSchema, exportColumnConfig]);

  // ── Contador en vivo + tomas incluidas ──────────────────────────────────────
  const [count, setCount] = useState<number | null>(null);
  const [tomasCount, setTomasCount] = useState<number>(0);
  const [countLoading, setCountLoading] = useState(false);

  const [filename, setFilename] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [exportProgress, setExportProgress] = useState(0); // 0-100 (progreso real del server via poll)
  const [exportEtaSec, setExportEtaSec] = useState<number | null>(null); // ETA real (throughput server)
  const [exportComplete, setExportComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PT-#64 — bump tras un export OK para refrescar el panel de historial (ExportJobsLog).
  const [logRefreshKey, setLogRefreshKey] = useState(0);

  // ── Caja de filtros TRENDS: options + caps por dimensión ────────────────────
  const [availableSites, setAvailableSites] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableSubcategories, setAvailableSubcategories] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  const [subcategoryFilterEnabled, setSubcategoryFilterEnabled] = useState(false);
  const [segmentFilterEnabled, setSegmentFilterEnabled] = useState(false);

  // Selección viva por dimensión (source of truth del acotado; espejo de la caja).
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);

  const [showFilters, setShowFilters] = useState(true);

  // Parámetros de resolución de tomas para las llamadas al backend.
  // Rango Personalizado inválido: Hasta anterior a Desde (mismo guard que Trends).
  const invalidCustomRange = mode === 'period' && periodPreset === 'custom'
    && !!dateFrom && !!dateTo && dateTo < dateFrom;

  const resolutionParams = useMemo<Record<string, string>>(() => {
    const p: Record<string, string> = {};
    if (mode === 'count') {
      p.tomasLimit = String(tomasLimit);
    } else if (periodPreset === 'custom' || periodPreset === 'current_month') {
      // Intervalo / Mes actual: mandar dateFrom/dateTo (resolveLimitedTomas los prioriza).
      if (dateFrom) p.dateFrom = dateFrom;
      if (dateTo) p.dateTo = dateTo;
    } else {
      p.periodPreset = periodPreset;
    }
    return p;
  }, [mode, tomasLimit, periodPreset, dateFrom, dateTo]);

  // ── Cargar schema (columnas a exportar) de la toma actual ────────────────────
  useEffect(() => {
    if (!tableName) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/tables/${tableName}/schema`);
        if (cancelled) return;
        const schema: any[] = Array.isArray(res.data?.schema) ? res.data.schema : [];
        const columnConfig = res.data?.columnConfig || null;
        // Guardamos schema + columnConfig crudos; las columnas de export se derivan con
        // buildExportColumns (MISMO criterio que PUBS: core lógico→físico + native merge catalog).
        setExportSchema(schema);
        setExportColumnConfig(columnConfig);
      } catch (e: any) {
        if (!cancelled) {
          console.error('[ExportPanel] error cargando schema:', e);
          setError(t('export.error_schema', 'No se pudieron cargar las columnas de la toma.'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tableName, t]);

  // ── Cargar options + caps per-cliente para la caja (mismos endpoints distinct-*
  //    que Tendencias/Publicaciones; universo multi-toma, NO facets per-toma) ────
  useEffect(() => {
    if (!tableName) return;
    let cancelled = false;

    (async () => {
      // 1. Caps subcategoría/segmento desde columnConfig.
      let subEnabled = false;
      let segEnabled = false;
      // PT-#91: columnConfig + schema también deciden la fuente del desplegable de Marca.
      let brandCc: any = null;
      let brandSchema: any[] | null = null;
      try {
        const schemaRes = await api.get(`/api/tables/${tableName}/schema`);
        const cc = (schemaRes.data && (schemaRes.data as any).columnConfig) || null;
        brandCc = cc;
        brandSchema = Array.isArray((schemaRes.data as any)?.schema) ? (schemaRes.data as any).schema : null;
        const layout = cc?.filterLayout;
        const enabledInLayout = (key: string): boolean | undefined => {
          if (!layout?.rows) return undefined;
          for (const row of layout.rows) {
            const field = row.fields?.find((f: any) => f.key === key);
            if (field) return field.enabled;
          }
          return false;
        };
        subEnabled = !!(cc?.core?.subcategory?.filterable) && (enabledInLayout('subcategory') ?? true);
        segEnabled = !!(cc?.core?.segmento?.filterable) && (enabledInLayout('segment') ?? true);
      } catch { /* sin schema → caps false (colapsan) */ }
      if (cancelled) return;
      setSubcategoryFilterEnabled(subEnabled);
      setSegmentFilterEnabled(segEnabled);

      // 2. Sitios.
      try {
        const res = await api.get(`/api/tables/${tableName}/distinct-sites`);
        if (!cancelled) setAvailableSites(res.data.sites || []);
      } catch { /* noop */ }

      // 3. Marca. PT-#91: utils/brandOptionsSource. Sin merge de marca (Adidas, LG) =
      //    /distinct/<Marca> (columna de la toma, misma fuente que Publicaciones); con merge
      //    (Samsung, TIA) se mantiene tags MARCA joineados a la toma.
      try {
        const res = await api.get(brandOptionsPath(tableName, brandCc, brandSchema, selectedClient?.enable_tags !== false));
        const raw: string[] = Array.isArray(res.data) ? res.data : parseDistinctOptions(res.data);
        const brands = raw
          .filter((v): v is string => typeof v === 'string' && v !== '')
          .sort((a, b) => {
            if (a === '(vacío)') return 1;
            if (b === '(vacío)') return -1;
            return a.localeCompare(b);
          });
        if (!cancelled && brands.length) setAvailableBrands(brands);
      } catch { /* noop */ }

      // 4. Subcategoría + segmento (solo si el cliente los habilita).
      if (subEnabled) {
        try {
          const res = await api.get(`/api/tables/${tableName}/distinct-subcategory`);
          if (!cancelled) setAvailableSubcategories(parseDistinctOptions(res.data));
        } catch { /* noop */ }
      } else if (!cancelled) {
        setAvailableSubcategories([]);
      }
      if (segEnabled) {
        try {
          const res = await api.get(`/api/tables/${tableName}/distinct-segmento`);
          if (!cancelled) setAvailableSegments(parseDistinctOptions(res.data));
        } catch { /* noop */ }
      } else if (!cancelled) {
        setAvailableSegments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [tableName, selectedClient?.enable_tags]);

  // Cascade dimensional (category/subcategory/segment): facets reales de la toma para la
  // co-ocurrencia que treeMode usa para acotar hijas. Las options de category también salen
  // de acá (el facet resuelve category vía el MISMO path — tag/catalog — que el filtro del
  // backend; distinct-categories leería la columna física cruda y no matchearía en Samsung).
  const facetFilters = useMemo(() => ({
    brand: selectedBrands.join('|'),
    site: selectedSites.join('|'),
    category: selectedCategories.join('|'),
    subcategory: selectedSubcategories.join('|'),
    segment: selectedSegments.join('|'),
  }), [selectedBrands, selectedSites, selectedCategories, selectedSubcategories, selectedSegments]);

  const exportFacets = useFacetedFilters(facetFilters, {
    tableName,
    enabled: !!tableName,
    dimensions: ['category', 'subcategory', 'segment'],
    debounceMs: 300,
  });

  const inlineData = useMemo<InlineSourceData>(() => ({
    brand: { options: availableBrands, meta: null, enabled: true },
    site: { options: availableSites, meta: null, enabled: true },
    category: { options: exportFacets.getOptions('category'), meta: exportFacets.getMeta('category'), enabled: true },
    subcategory: { options: availableSubcategories, meta: exportFacets.getMeta('subcategory'), enabled: subcategoryFilterEnabled },
    segment: { options: availableSegments, meta: exportFacets.getMeta('segment'), enabled: segmentFilterEnabled },
  }), [availableBrands, availableSites, availableSubcategories, subcategoryFilterEnabled,
       availableSegments, segmentFilterEnabled, exportFacets]);

  const {
    dimensions: filterDimensions,
    activeFilters: boxActiveFilters,
    setDimFilter,
  } = useFilterBoxState({
    config: EXPORTAR_CONFIG,
    tableName,
    enabled: true,
    inlineData,
  });

  // PT-#61 — Ref al setter de dimensiones de la caja. applySnapshot lo usa para REHIDRATAR la
  // caja al cargar un filtro guardado: sin esto la caja seguiría mostrando la selección vieja
  // mientras el export ya usa las dims cargadas (mismo desync box→state que el QA T5 de Trends).
  const setDimFilterRef = useRef<((dim: DimensionKey, value: string[]) => void) | null>(null);
  setDimFilterRef.current = setDimFilter;

  // Sync selección de la caja → state que arma el `filters` del export (source of truth).
  const syncDim = (key: DimensionKey, setter: (v: string[]) => void, current: string[]) => {
    const next = boxActiveFilters[key] || [];
    if (next.length !== current.length || next.some((v, i) => v !== current[i])) setter(next);
  };
  useEffect(() => { syncDim('site', setSelectedSites, selectedSites); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('brand', setSelectedBrands, selectedBrands); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('category', setSelectedCategories, selectedCategories); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('subcategory', setSelectedSubcategories, selectedSubcategories); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('segment', setSelectedSegments, selectedSegments); /* eslint-disable-next-line */ }, [boxActiveFilters]);

  // PT-#61 — Bridge de saved filters (espejo del de Tendencias). Registramos un getter
  // (snapshot de las 5 dims + alcance temporal) + un applier (aplica un snapshot cargado y
  // rehidrata la caja). Dashboard los invoca al guardar/cargar un filtro con scope 'multi'.
  // Undefined-safe: cada campo del snapshot es opcional → filtros legacy sin ejes de export
  // no rompen (el applier ignora los campos ausentes y deja el estado actual).
  useEffect(() => {
    const unregister = registerExportBridge({
      getSnapshot: (): ExportAxesSnapshot => ({
        sites: selectedSites,
        brands: selectedBrands,
        categories: selectedCategories,
        subcategories: selectedSubcategories,
        segments: selectedSegments,
        mode,
        tomasLimit,
        periodPreset,
        dateFrom,
        dateTo,
      }),
      applySnapshot: (snap: ExportAxesSnapshot) => {
        // Solo aplicamos los campos presentes (retrocompat con filtros viejos). Para las 5
        // dimensiones seteamos el state "real" (fuente del count/export) Y rehidratamos la caja
        // (setDimFilterRef) para que pastillas/dropdowns reflejen lo cargado — cierra el desync
        // box→state. El guard de syncDim evita loop (mismo valor → no-op).
        const rehydrate = setDimFilterRef.current;
        if (Array.isArray(snap.sites)) { setSelectedSites(snap.sites); rehydrate?.('site', snap.sites); }
        if (Array.isArray(snap.brands)) { setSelectedBrands(snap.brands); rehydrate?.('brand', snap.brands); }
        if (Array.isArray(snap.categories)) { setSelectedCategories(snap.categories); rehydrate?.('category', snap.categories); }
        if (Array.isArray(snap.subcategories)) { setSelectedSubcategories(snap.subcategories); rehydrate?.('subcategory', snap.subcategories); }
        if (Array.isArray(snap.segments)) { setSelectedSegments(snap.segments); rehydrate?.('segment', snap.segments); }
        if (snap.mode) setMode(snap.mode);
        if (typeof snap.tomasLimit === 'number') setTomasLimit(snap.tomasLimit);
        if (typeof snap.periodPreset === 'string') setPeriodPreset(snap.periodPreset);
        if (typeof snap.dateFrom === 'string') setDateFrom(snap.dateFrom);
        if (typeof snap.dateTo === 'string') setDateTo(snap.dateTo);
        // El count se recalcula solo vía el useEffect sobre resolutionParams/filtersKey.
      },
    });
    return unregister;
  }, [selectedSites, selectedBrands, selectedCategories, selectedSubcategories, selectedSegments,
      mode, tomasLimit, periodPreset, dateFrom, dateTo]);

  // Mapeo dimensiones → `filters` (shape de Pubs que consume getTableDataOptimized).
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (selectedBrands.length) f[brandFilterColumn] = selectedBrands.join('|');
    if (selectedSites.length) f['sitio'] = selectedSites.join('|');
    if (selectedCategories.length) f[categoryFilterColumn] = selectedCategories.join('|');
    if (selectedSubcategories.length) f['subcategory'] = selectedSubcategories.join('|');
    if (selectedSegments.length) f['segmento'] = selectedSegments.join('|');
    return f;
  }, [selectedBrands, selectedSites, selectedCategories, selectedSubcategories, selectedSegments,
      brandFilterColumn, categoryFilterColumn]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  // ── Contador "N filas" (single source of truth desde el backend) ─────────────
  const countDebounceRef = useRef<any>(null);
  const customIncomplete = mode === 'period' && periodPreset === 'custom' && (!dateFrom || !dateTo);
  useEffect(() => {
    if (!tableName) return;
    // Modo Personalizado: no estimar hasta tener rango válido y completo (evita fallback silencioso).
    if (invalidCustomRange || customIncomplete) { setCount(null); setTomasCount(0); setCountLoading(false); return; }
    if (countDebounceRef.current) clearTimeout(countDebounceRef.current);
    setCountLoading(true);
    countDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams(resolutionParams);
        params.set('filters', filtersKey);
        const res = await api.get(`/api/export/multi-toma-count?${params.toString()}`);
        setCount(typeof res.data?.total === 'number' ? res.data.total : 0);
        setTomasCount(typeof res.data?.tomasCount === 'number' ? res.data.tomasCount : 0);
      } catch (e: any) {
        console.error('[ExportPanel] error estimando filas:', e);
        setCount(null);
        setTomasCount(0);
      } finally {
        setCountLoading(false);
      }
    }, 400);
    return () => { if (countDebounceRef.current) clearTimeout(countDebounceRef.current); };
  }, [tableName, resolutionParams, filtersKey]);

  const defaultFilename = useMemo(() => {
    let scope: string;
    if (mode === 'count') scope = `${tomasLimit}tomas`;
    else if (periodPreset === 'custom') scope = `${dateFrom || 'ini'}_a_${dateTo || 'fin'}`;
    else scope = periodPreset;
    return `export_${scope}_${new Date().toISOString().slice(0, 10)}`;
  }, [mode, tomasLimit, periodPreset, dateFrom, dateTo]);

  // ── Export ───────────────────────────────────────────────────────────────────
  // PT-#61 (2026-08-20): UN SOLO generador = streaming server-side (POST /export/multi-toma-stream).
  // TODO el export pasa por acá sin importar el tamaño (corrección de Gabriel). El server arma el
  // XLSX fila-por-fila con exceljs (memoria constante) y lo streamea; el browser recibe bytes → Blob
  // binario (fuera del heap JS) → descarga. Esto evita el `RangeError: Invalid string length` que
  // reventaba el armado client-side (xlsx-js-style construye toda la planilla como un string y topa
  // el límite de V8 ~512MB en cientos de miles de filas). El formato es IDÉNTICO al de exportToExcel.ts
  // (ID Pulpou, URL/Imagen HYPERLINK, currency es-AR, headers estilados) — ver exportRoutes.js.
  const handleExport = useCallback(async () => {
    if (exportColumns.length === 0) {
      setError(t('export.error_no_columns', 'No hay columnas habilitadas para exportar.'));
      return;
    }
    setError(null);
    setExporting(true);
    setExportComplete(false);
    setExportProgress(0);
    setExportEtaSec(null);
    setExportMessage(t('export.step_streaming_start', 'Generando archivo en el servidor...'));

    // jobId para el poll de progreso (emitted/total desde el server).
    const jobId = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `job_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    let finished = false;
    let lastEmitted = 0;

    // Resolución de tomas en el shape que parsea el server (tomasLimit | periodPreset | dateFrom/To).
    const resolutionBody: Record<string, any> = {};
    if (mode === 'count') resolutionBody.tomasLimit = tomasLimit;
    else if (periodPreset === 'custom') {
      if (dateFrom) resolutionBody.dateFrom = dateFrom;
      if (dateTo) resolutionBody.dateTo = dateTo;
    } else resolutionBody.periodPreset = periodPreset;

    // Columnas serializables para el server: ID Pulpou + Toma prepend, luego las MISMAS columnas
    // que PUBS (via buildExportColumns.stream), con URL inyectada post-título (igual que el cliente).
    // PT-#70: cada columna resuelve title/format(enum)/widthChars vía resolveExportAttrs
    // (cadena template ?? columnConfig ?? DEFAULT[id] ?? hardcoded) y los manda al server.
    const cc = exportColumnConfig || {};
    const tplItems: any[] = cc?.templates?.data_export?.items || [];
    const tplExportById = new Map<string, any>(
      tplItems.filter((it: any) => it && it.export).map((it: any) => [it.id, it.export])
    );
    const ccExportFor = (id: string) =>
      cc?.core?.[id]?.export ?? cc?.native?.[id]?.export ?? cc?.tags?.[id]?.export ?? null;
    const streamColumns: any[] = [];
    // fallbackHeader = displayName/label vigente; el title final lo dicta resolveExportAttrs
    // (title del template/columnConfig/DEFAULT[id] gana sobre el fallback).
    const pushResolved = (rawId: string, kind: string, fallbackHeader: string, field?: string) => {
      // PT-#70 #4: normalizar id físico/nativo → lógico (discount→descuento, segment→segmento,
      // udm_source→udm) para que resuelva a DEFAULT_EXPORT_ATTRS (title '% Desc', format percent).
      const logicalId = toLogicalExportId(rawId);
      const attrs = resolveExportAttrs(logicalId, {
        templateExport: tplExportById.get(logicalId) ?? null,
        columnConfigExport: ccExportFor(logicalId),
        fallbackTitle: fallbackHeader,
      });
      streamColumns.push({
        header: attrs.title,
        format: attrs.format,
        width: attrs.widthChars,
        kind,
        ...(field !== undefined ? { field } : {}),
      });
    };

    // PT-#67: las calculadas (id_pulpou/toma/url) ya viven en `exportColumns` (inyectadas
    // por injectDataExportCalculadas + ordenadas por template/DATA_EXPORT_BASE_ORDER). El
    // stream se arma iterando SOLO el template — sin inyección hardcodeada.
    for (const col of exportColumns) {
      const s = col.stream;
      if (!s) continue;
      const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
      pushResolved(col.id, s.kind, header, s.field);
    }

    const name = (filename.trim() || defaultFilename);

    // Poll de progreso real: emitted/total del server → % + ETA por throughput medido.
    const pollProgress = async () => {
      while (!finished) {
        try {
          const pr = await api.get(`/api/export/progress/${jobId}`);
          const emitted = pr.data?.emitted || 0;
          const total = pr.data?.total || 0;
          const elapsedMs = pr.data?.elapsedMs || 0;
          lastEmitted = emitted;
          if (total > 0) {
            setExportProgress(Math.min(99, Math.round((emitted / total) * 100)));
            if (emitted > 0 && elapsedMs > 0) {
              const rate = emitted / (elapsedMs / 1000); // filas/s reales
              const remaining = Math.max(0, total - emitted);
              setExportEtaSec(rate > 0 ? Math.ceil(remaining / rate) : null);
            }
            setExportMessage(t('export.step_streaming', 'Generando: {{done}} de {{total}} filas', {
              done: emitted.toLocaleString('es-AR'), total: total.toLocaleString('es-AR'),
            }));
          }
          if (pr.data?.done) break;
        } catch { /* 404 hasta que el job se registra; seguir intentando */ }
        await new Promise(r => setTimeout(r, 1000));
      }
    };

    try {
      // PT-#75: el server ya NO devuelve el XLSX en la respuesta (lo cortaba el cap de 32 MiB
      // del proxy). Devuelve un stream NDJSON de heartbeat + una línea final con la signed URL
      // para bajar el archivo directo de GCS. Leemos como texto y parseamos la última línea.
      const postPromise = apiLongOperation.post('/api/export/multi-toma-stream', {
        ...resolutionBody,
        filters,
        columns: streamColumns,
        filename: name,
        jobId,
        estimatedTotal: count ?? 0,
        // PT-#75: baseURL directo a Cloud Run (esquiva el cap de ~60s del edge de Hosting que
        // corta el stream NDJSON). En local es el backend local. Ver exportStreamBaseURL en api.ts.
      }, { responseType: 'text', baseURL: exportStreamBaseURL });

      pollProgress(); // fire-and-forget; se corta con `finished`.

      const res = await postPromise;
      finished = true;

      // Parsear el NDJSON: la última línea no vacía trae { done, downloadUrl, error }.
      const text = typeof res.data === 'string' ? res.data : String(res.data ?? '');
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      let final: any = null;
      for (let i = lines.length - 1; i >= 0; i--) {
        try { const obj = JSON.parse(lines[i]); if (obj && obj.done) { final = obj; break; } } catch { /* línea de heartbeat parcial */ }
      }
      if (!final) throw new Error('respuesta del servidor incompleta (sin línea final)');
      if (final.error) throw new Error(final.error);
      if (!final.downloadUrl) throw new Error('el servidor no devolvió URL de descarga');

      // Descargar directo de GCS: la signed URL trae Content-Disposition attachment con el
      // filename, así que el browser baja el archivo sin navegar la página.
      const a = document.createElement('a');
      a.href = final.downloadUrl;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();

      const finalN = (final.rowCount || lastEmitted || count || 0);
      setExportProgress(100);
      setExportEtaSec(0);
      setExportComplete(true);
      setExportMessage(t('export.step_done', 'Listo: {{n}} filas exportadas.', { n: finalN.toLocaleString('es-AR') }));
      // Refrescar el historial: el job recién quedó registrado en el server (status=done).
      setLogRefreshKey(k => k + 1);
    } catch (e: any) {
      finished = true;
      console.error('[ExportPanel] error exportando:', e);
      // El server puede responder el error como JSON (500 temprano) o como línea NDJSON final.
      let msg = e?.message || '';
      const errData = e?.response?.data;
      if (typeof errData === 'string') {
        try { msg = JSON.parse(errData)?.error || msg; } catch { /* no era JSON */ }
      } else if (errData && typeof errData === 'object') {
        msg = errData.error || msg;
      }
      setError(msg || t('export.error_generic', 'Error al exportar. Reintentá.'));
    } finally {
      finished = true;
      setExporting(false);
    }
  }, [exportColumns, mode, tomasLimit, periodPreset, dateFrom, dateTo, filters, filename, defaultFilename, count, t]);

  // Slot `period` (alcance temporal) DENTRO de la caja de filtros, misma grilla que Trends.
  const slots = useMemo<Partial<Record<FilterControlKey, FilterSlot>>>(() => ({
    period: (_widthStyle) => {
      const cell: React.CSSProperties = { width: 'calc(25% - 1rem)', minWidth: '180px' };
      return (
        <div style={{ width: '100%' }}>
          <div className="flex gap-4 flex-wrap">
            <div style={cell}>
              <FieldLabel icon={<SlidersHorizontal className="h-3.5 w-3.5" />}>
                {t('export.scope', 'Acotar por')}
              </FieldLabel>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as TomaMode)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="count">{t('export.by_count', 'Tomas')}</option>
                <option value="period">{t('export.by_period', 'Período')}</option>
              </select>
            </div>

            {mode === 'count' ? (
              <div style={cell}>
                <FieldLabel icon={<Hash className="h-3.5 w-3.5" />}>
                  {t('export.tomas_count', 'Cantidad de tomas')}
                </FieldLabel>
                {/* PT-#72: input numérico editable 1–90 (default 5), reemplaza la lista fija. */}
                <input
                  type="number"
                  min={TOMAS_LIMIT_MIN}
                  max={TOMAS_LIMIT_MAX}
                  step={1}
                  value={tomasLimit}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (Number.isNaN(n)) { setTomasLimit(TOMAS_LIMIT_MIN); return; }
                    setTomasLimit(Math.min(TOMAS_LIMIT_MAX, Math.max(TOMAS_LIMIT_MIN, n)));
                  }}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums"
                />
              </div>
            ) : (
              <>
                <div style={cell}>
                  <FieldLabel icon={<CalendarRange className="h-3.5 w-3.5" />}>
                    {t('export.period', 'Período')}
                  </FieldLabel>
                  <select
                    value={periodPreset}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPeriodPreset(v);
                      // "Mes actual": auto-rellenar Desde=1° y Hasta=último día del mes (read-only).
                      if (v === 'current_month') {
                        setDateFrom(firstDayOfCurrentMonthYmd());
                        setDateTo(lastDayOfCurrentMonthYmd());
                      }
                    }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {PERIOD_PRESETS.map(p => (
                      <option key={p.value} value={p.value}>{t(p.labelKey, p.fallback)}</option>
                    ))}
                  </select>
                </div>

                {(periodPreset === 'custom' || periodPreset === 'current_month') && (
                  <>
                    {(() => {
                      // Mes actual dicta las fechas → inputs read-only (el modo auto-rellena).
                      const dateLocked = periodPreset === 'current_month';
                      const dateInputCls = `w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums${dateLocked ? ' bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`;
                      return (
                        <>
                          <div style={cell}>
                            <FieldLabel icon={<Calendar className="h-3.5 w-3.5" />}>{t('export.date_from', 'Desde')}</FieldLabel>
                            <input
                              type="date"
                              value={dateFrom}
                              max={dateTo || undefined}
                              disabled={dateLocked}
                              onChange={(e) => setDateFrom(e.target.value)}
                              className={dateInputCls}
                            />
                          </div>
                          <div style={cell}>
                            <FieldLabel icon={<Calendar className="h-3.5 w-3.5" />}>{t('export.date_to', 'Hasta')}</FieldLabel>
                            <input
                              type="date"
                              value={dateTo}
                              min={dateFrom || undefined}
                              disabled={dateLocked}
                              onChange={(e) => setDateTo(e.target.value)}
                              className={dateInputCls}
                            />
                          </div>
                        </>
                      );
                    })()}
                    {invalidCustomRange && (
                      <div className="flex items-end" style={cell}>
                        <p className="text-xs text-red-600">
                          {t('export.invalid_range', 'La fecha "Hasta" no puede ser anterior a "Desde".')}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      );
    },
  }), [t, mode, tomasLimit, periodPreset, dateFrom, dateTo, invalidCustomRange]);

  if (!tableName) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center py-12">
          <FileSpreadsheet className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t('export.title', 'Exportar')}
          </h2>
          <p className="text-gray-600">{t('dashboard.select_data_set', 'Seleccioná un conjunto de datos')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Encabezado de sección */}
      <div className="flex items-center gap-2">
        <FileSpreadsheet className="h-5 w-5 text-pink-500" />
        <h2 className="text-lg font-semibold text-gray-800">{t('export.title', 'Exportar')}</h2>
        <span className="text-sm text-gray-400">
          {t('export.subtitle', 'Descargá las filas de varias tomas en un único XLSX')}
        </span>
      </div>

      {/* Caja de filtros TRENDS (dimensiones combinables + cascade) + alcance temporal (slot) */}
      <FilterBox
        config={EXPORTAR_CONFIG}
        t={t}
        dimensions={filterDimensions}
        slots={slots}
        loading={exportFacets.loading}
        showHeader
        headerTitle={t('filters.title', 'Filtros')}
        onOpenSavedFilters={onOpenSavedFilters}
        savedFiltersLoading={loadingSavedFilters}
        collapsed={!showFilters}
        onToggleCollapse={() => setShowFilters(!showFilters)}
      />

      {/* Resultado + acción primaria única */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Estimado: total de registros + cantidad de tomas */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 flex flex-col justify-center gap-3">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-800 tabular-nums">
                  {countLoading ? '—' : (count != null ? count.toLocaleString('es-AR') : '—')}
                </span>
                <span className="text-sm text-gray-500">{t('export.estimatedRows', 'registros estimados')}</span>
                {countLoading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />}
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-xl font-semibold text-gray-700 tabular-nums">
                  {countLoading ? '—' : tomasCount.toLocaleString('es-AR')}
                </span>
                <span className="text-sm text-gray-500">
                  {tomasCount === 1 ? t('export.tomaSingular', 'toma') : t('export.tomasPlural', 'tomas')}
                </span>
              </div>
            </div>
            {(count ?? 0) > MAX_EXPORT_ROWS && (
              <p className="text-xs text-amber-600">
                {t('export.warn_over_limit', 'Supera el límite de {{max}} filas: se exportarán las primeras {{max}}.', { max: MAX_EXPORT_ROWS.toLocaleString('es-AR') })}
              </p>
            )}
          </div>

          {/* Nombre de archivo + botón */}
          <div className="flex flex-col justify-between">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {t('export.filename', 'Nombre del archivo')}
              </label>
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder={defaultFilename}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                {t('export.filenameHint', 'La extensión .xlsx se agrega sola')}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {t('export.all_columns_hint', 'Se exportan todas las columnas habilitadas del cliente ({{n}}).', { n: exportColumns.length })}
              </p>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500 min-h-[1.25rem]">
                {error && <span className="text-red-600">{error}</span>}
              </div>
              <button
                onClick={handleExport}
                disabled={exporting || countLoading || (count ?? 0) === 0 || exportColumns.length === 0 || invalidCustomRange || customIncomplete}
                className="flex items-center gap-2 px-5 py-2.5 bg-pink-500 text-white rounded-md text-sm font-medium hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {t('export.export_xlsx', 'Exportar XLSX')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PT-#64 — Historial de exportaciones (persistente por-cliente) debajo de la caja */}
      <ExportJobsLog refreshKey={logRefreshKey} />

      <ProgressToast
        isOpen={exporting || exportComplete}
        onClose={() => { setExportComplete(false); setExportMessage(''); }}
        progress={exportProgress}
        message={exportMessage}
        isComplete={exportComplete}
        totalRows={count ?? undefined}
        realProgress
        etaSecOverride={exportEtaSec}
      />
    </div>
  );
};

export default ExportPanel;
